import React, { useState, useEffect } from "react";
import { Loader, Statistic, Dropdown, Icon } from "semantic-ui-react";
import { filter, sumBy, map } from "lodash";
import moment from "moment";
import {
  HeaderPage,
  PrintHeader, PrintTable,
  TablePayments,
  TablePaymentsPrintColumns,
} from "../../components/Admin";
import { usePayment, usePrintOrientation } from "../../hooks";
import { getBusinessToday } from "../../utils/date";
import { PAYMENT_METHODS, getPaymentAmounts } from "../../utils/constants";

const TYPE_OPTIONS = map(PAYMENT_METHODS, (method) => ({
  key: method.value,
  value: method.value,
  text: method.text,
}));

// Color de cada tarjeta de totales (Semantic UI Statistic).
const METHOD_COLORS = { CASH: "green", CARD: "blue", YAPE: "purple", PLIN: "teal" };

const VIEW_OPTIONS = [
  { key: "summary", value: "summary", text: "Resumido" },
  { key: "detailed", value: "detailed", text: "Detallado" },
];


export function DailySummary() {
  const [dateFrom, setDateFrom] = useState(getBusinessToday);
  const [dateTo, setDateTo] = useState(getBusinessToday);
  const [typeView, setTypeView] = useState("CASH");
  const [viewMode, setViewMode] = useState("summary");
  const { loading, payments, getPaymentsInRange } = usePayment();
  usePrintOrientation("portrait");

  useEffect(() => {
    if (dateFrom && dateTo) getPaymentsInRange(dateFrom, dateTo);
  }, [dateFrom, dateTo]);

  // Un pago mixto aparece en el detalle de cada forma de pago que use, y en
  // cada total suma solo su parte (ver getPaymentAmounts).
  const paymentsOf = (type) =>
    filter(payments, (payment) => payment.paymentType !== "LOSS" && getPaymentAmounts(payment)[type]);
  const totalOf = (type) =>
    sumBy(paymentsOf(type), (payment) => getPaymentAmounts(payment)[type]);
  const lossTotal = sumBy(
    filter(payments, (payment) => payment.paymentType === "LOSS"),
    (payment) => Number(payment.totalPayment)
  );

  const filteredPayments = paymentsOf(typeView);

  const dateRangeLabel = `Del ${moment(dateFrom).format("DD/MM/YYYY")} al ${moment(
    dateTo
  ).format("DD/MM/YYYY")}`;

  return (
    <>
      <div className="screen-only">
        <HeaderPage
          title="Resumen Diario"
          btnTitle={
            <>
              <Icon name="print" /> Imprimir
            </>
          }
          btnClick={() => window.print()}
        />
      </div>

      <div
        className="screen-only"
        style={{ display: "flex", gap: "15px", margin: "10px 0 20px" }}
      >
        <div>
          <label style={{ display: "block", marginBottom: 4 }}>Desde</label>
          <input
            type="date"
            value={dateFrom}
            max={dateTo}
            onChange={(e) => setDateFrom(e.target.value)}
          />
        </div>
        <div>
          <label style={{ display: "block", marginBottom: 4 }}>Hasta</label>
          <input
            type="date"
            value={dateTo}
            min={dateFrom}
            onChange={(e) => setDateTo(e.target.value)}
          />
        </div>
        <div>
          <label style={{ display: "block", marginBottom: 4 }}>Tipo de reporte</label>
          <Dropdown
            selection
            options={VIEW_OPTIONS}
            value={viewMode}
            onChange={(_, data) => setViewMode(data.value)}
          />
        </div>
      </div>

      <PrintTable>
        <PrintHeader title="Resumen Diario" subtitle={dateRangeLabel}>
          <TablePaymentsPrintColumns />
        </PrintHeader>

        {loading ? (
          <Loader active inline="centered">
            Cargando...
          </Loader>
        ) : (
          <div className="print-content">
            <Statistic.Group widths="five" size="small" style={{ marginBottom: "25px" }}>
              {map(PAYMENT_METHODS, (method) => (
                <Statistic key={method.value} color={METHOD_COLORS[method.value]}>
                  <Statistic.Value>S/ {totalOf(method.value).toFixed(2)}</Statistic.Value>
                  <Statistic.Label>{method.text}</Statistic.Label>
                </Statistic>
              ))}
              <Statistic color="orange">
                <Statistic.Value>S/ {lossTotal.toFixed(2)}</Statistic.Value>
                <Statistic.Label>Pérdidas</Statistic.Label>
              </Statistic>
            </Statistic.Group>

            <div className="screen-only">
              <Dropdown
                selection
                options={TYPE_OPTIONS}
                value={typeView}
                onChange={(_, data) => setTypeView(data.value)}
                style={{ marginBottom: "15px" }}
              />
              <TablePayments
                payments={filteredPayments}
                detailed={viewMode === "detailed"}
              />
            </div>

            <div className="print-only">
              {map(PAYMENT_METHODS, (method) => (
                <React.Fragment key={method.value}>
                  <h4>Detalle {method.text}</h4>
                  <TablePayments
                    payments={paymentsOf(method.value)}
                    detailed={viewMode === "detailed"}
                  />
                </React.Fragment>
              ))}
            </div>
          </div>
        )}
      </PrintTable>
    </>
  );
}
